import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Lang } from './types';

const STORAGE_KEY = 'cji-lang';

type LanguageContextValue = {
  readonly lang: Lang;
  readonly setLang: (lang: Lang) => void;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

const isLang = (value: unknown): value is Lang =>
  value === 'ja' || value === 'zh';

/**
 * Provides the active UI language.
 *
 * The prerendered HTML is always Japanese, so the initial render (SSR and the
 * first client render) must stay `'ja'` to avoid a hydration mismatch. The
 * stored preference is read after mount and applied on the next render.
 */
export const LanguageProvider = ({
  children,
}: {
  readonly children: ReactNode;
}): JSX.Element => {
  const [lang, setLangState] = useState<Lang>('ja');

  // Restore the persisted preference after hydration.
  useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLang(stored) && stored !== 'ja') {
      setLangState(stored);
    }
  }, []);

  // Keep <html lang> in sync (affects font selection and assistive tech).
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Lang): void => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage may be unavailable (private mode); the choice still applies for this session.
    }
  }, []);

  const value = useMemo<LanguageContextValue>(
    () => ({ lang, setLang }),
    [lang, setLang],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = (): LanguageContextValue => {
  const context = useContext(LanguageContext);
  if (context === null) {
    throw new Error('useLang must be used within a LanguageProvider.');
  }
  return context;
};
